import React, { useState, useEffect } from 'react';
import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@t-one-internal/design-system';
import type { ReportConfig, ReportType, ReportModule, ReportTypeOption } from './types';
import ColumnConfigurator from './components/ColumnConfigurator';
import DateRangePicker from './components/DateRangePicker';
import Scheduler from './components/Scheduler';
import { Save, Layout, CalendarClock, Loader2, AlertCircle } from 'lucide-react';
import { customReportsApi } from '../../../api/customReportsApi';

interface CustomReportBuilderProps {
    module: ReportModule;
    initialConfig?: ReportConfig;
    onSave: (config: ReportConfig) => void;
    onCancel: () => void;
}

const createDefaultConfig = (module: ReportModule): ReportConfig => ({
    module,
    name: '',
    description: '',
    type: '',
    dateRange: {
        type: '30d',
        startDate: '',
        endDate: new Date().toISOString().split('T')[0]
    },
    columns: [],
    schedule: {
        frequency: 'weekly',
        time: '09:00',
        dayOfWeek: 'monday',
        monthlyOrdinal: 'first',
        monthlyDayType: 'day',
        recipients: []
    }
});

const CustomReportBuilder: React.FC<CustomReportBuilderProps> = ({ module, initialConfig, onSave, onCancel }) => {
    const [config, setConfig] = useState<ReportConfig>(createDefaultConfig(module));
    const [reportTypes, setReportTypes] = useState<ReportTypeOption[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingSchema, setIsLoadingSchema] = useState(false);

    // Fetch schema for a report type
    const loadSchema = async (reportKey: string) => {
        if (!reportKey) return;

        try {
            setIsLoadingSchema(true);
            const schema = await customReportsApi.fetchReportSchema(reportKey);
            const columns = customReportsApi.transformSchemaColumns(schema);
            setConfig(prev => ({
                ...prev,
                columns
            }));
        } catch (error) {
            console.error('Failed to load report schema:', error);
        } finally {
            setIsLoadingSchema(false);
        }
    };

    useEffect(() => {
        const loadReportTypes = async () => {
            try {
                setIsLoading(true);
                const types = await customReportsApi.fetchReportTypes(module);
                setReportTypes(types);

                // Set default type if not editing and types are available
                if (!initialConfig && types.length > 0) {
                    const defaultType = types[0];
                    setConfig(prev => ({
                        ...prev,
                        type: defaultType.value,
                        description: defaultType.description
                    }));
                    // Fetch schema for the default type using metricKey
                    loadSchema(defaultType.metricKey);
                }
            } catch (error) {
                console.error('Failed to load report types:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadReportTypes();
    }, [module]);

    useEffect(() => {
        if (initialConfig) {
            setConfig(initialConfig);
        }
    }, [initialConfig]);

    // Update columns when report type changes
    const handleTypeChange = async (type: ReportType) => {
        const typeDef = reportTypes.find(t => t.value === type);
        setConfig(prev => ({
            ...prev,
            type,
            description: typeDef?.description || prev.description,
            columns: [] // Clear columns while loading
        }));

        // Fetch schema for the new type using metricKey
        if (typeDef?.metricKey) {
            await loadSchema(typeDef.metricKey);
        }
    };

    const handleSave = () => {
        onSave(config);
    };

    return (
        <div className="h-full bg-white flex flex-col font-sans overflow-y-auto">

            {/* Header */}
            <header className="bg-white flex-shrink-0">
                <div className="w-full px-6 h-16 flex items-center justify-between">
                    <div className="h-full flex items-center">
                        <p className="text-gray-500">Configure your report details, filters, and schedule.</p>
                    </div>
                    <div className="flex gap-3">
                        <Button
                            variant="outline"
                            onClick={onCancel}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                        >
                            <Save size={16} /> Save Report
                        </Button>
                    </div>
                </div>
            </header>

            <main className="flex-1 w-full p-6">

                <div className="flex flex-col gap-6">

                    {/* Main Layout Grid */}
                    <div className="grid grid-cols-5 gap-6 items-start">

                        {/* Left Column: General & Schedule */}
                        <div className="col-span-3 space-y-6">

                            {/* General Settings */}
                            {/* General Settings */}
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                                <div className="p-4 bg-gradient-to-r from-[#EEF2FF] via-[#FAF5FF] to-[#FDF2F8] border-b border-gray-200">
                                    <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">Report Configuration</h3>
                                </div>

                                <div className="p-6 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Report Name</label>
                                        <input
                                            type="text"
                                            value={config.name}
                                            onChange={(e) => setConfig({ ...config, name: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                                            placeholder="Enter report name"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
                                        <Select
                                            value={config.type}
                                            onValueChange={(val: string) => handleTypeChange(val as ReportType)}
                                            disabled={isLoading}
                                        >
                                            <SelectTrigger className="w-full h-11">
                                                <SelectValue placeholder={isLoading ? "Loading..." : "Select report type"} />
                                            </SelectTrigger>
                                            <SelectContent className="max-h-[350px]">
                                                {reportTypes.map(t => (
                                                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <p className="mt-2 text-sm text-gray-500 bg-gray-50 p-2 rounded">
                                            {reportTypes.find(t => t.value === config.type)?.description || 'Select a report type'}
                                        </p>
                                    </div>

                                    <DateRangePicker
                                        value={config.dateRange}
                                        onChange={(dr) => setConfig({ ...config, dateRange: dr })}
                                    />
                                </div>
                            </div>

                            {/* Delivery Schedule */}
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                                <div className="p-4 bg-gradient-to-r from-[#EEF2FF] via-[#FAF5FF] to-[#FDF2F8] border-b border-gray-200">
                                    <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                                        <CalendarClock size={16} /> Delivery Schedule
                                    </h3>
                                </div>
                                <div className="p-6 space-y-4">
                                    <Scheduler
                                        value={config.schedule}
                                        onChange={(sched) => setConfig({ ...config, schedule: sched })}
                                    />
                                    {config.dateRange.type === 'custom' && config.schedule.frequency !== 'once' && (
                                        <div className="flex items-start gap-2 p-3 bg-amber-50 text-amber-800 rounded-md text-xs border border-amber-200 animate-in fade-in slide-in-from-top-1">
                                            <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                                            <span>
                                                <span className="font-semibold">Warning:</span> This will be a recurring report for the same data set ({config.dateRange.startDate} - {config.dateRange.endDate}).
                                                Consider using relative dates (e.g., "Last 30 Days") for recurring schedules.
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                        </div>

                        {/* Right Column: Column Configuration */}
                        <div className="col-span-2">
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                                <div className="p-4 bg-gradient-to-r from-[#EEF2FF] via-[#FAF5FF] to-[#FDF2F8] border-b border-gray-200">
                                    <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                                        <Layout size={16} /> Column Management
                                        {isLoadingSchema && <Loader2 size={14} className="animate-spin text-indigo-500" />}
                                    </h3>
                                </div>
                                <div className="p-4">
                                    {isLoadingSchema ? (
                                        <div className="flex items-center justify-center py-8 text-gray-500">
                                            <Loader2 size={20} className="animate-spin mr-2" />
                                            Loading columns...
                                        </div>
                                    ) : config.columns.length === 0 ? (
                                        <div className="text-center py-8 text-gray-500">
                                            Select a report type to load available columns
                                        </div>
                                    ) : (
                                        <ColumnConfigurator
                                            columns={config.columns}
                                            onChange={(cols) => setConfig({ ...config, columns: cols })}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>

                    </div>

                </div>

            </main>

        </div>
    );
};

export default CustomReportBuilder;
